import { resolveApiAssetUrl } from "@/lib/backend";
import { cn } from "@/lib/utils";

type Props = {
  src: string | undefined | null;
  alt: string;
  /** Wrapper classes (border, radius, background). */
  frameClassName?: string;
  /** Extra classes on the img element. */
  className?: string;
  /**
   * Fixed aspect frame with object-contain (e.g. card thumbnails).
   * Pass `aspect-[16/10]` via frameClassName.
   */
  framed?: boolean;
  priority?: boolean;
};

/**
 * CMS image that never exceeds its parent width; scales any upload to fit inside the layout.
 */
export function CmsContainedImage({
  src,
  alt,
  frameClassName,
  className,
  framed = false,
  priority = false,
}: Props) {
  const resolved = resolveApiAssetUrl(src);
  if (!resolved) return null;

  const imgClass = cn(
    "max-w-full object-contain object-center",
    framed
      ? "absolute inset-0 h-full w-full"
      : "block h-auto w-full",
    className,
  );

  if (framed) {
    return (
      <div
        className={cn(
          "cms-media-frame relative w-full max-w-full overflow-hidden",
          frameClassName,
        )}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={resolved}
          alt={alt}
          decoding="async"
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : undefined}
          className={imgClass}
        />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "cms-media-frame w-full max-w-full overflow-hidden",
        frameClassName,
      )}
    >
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={resolved}
        alt={alt}
        decoding="async"
        loading={priority ? "eager" : "lazy"}
        fetchPriority={priority ? "high" : undefined}
        className={imgClass}
      />
    </div>
  );
}
